/* eslint-disable max-len */
import React, {
  useState, useEffect, useCallback, useMemo, useRef, useContext,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
} from 'reactstrap';

import { format, parseISO, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';

import AlertModal from '../../components/AlertModal';
import CustomTable from '../../components/CustomTable';
import DatePickerMaskedInput from '../../components/DatePickerMaskedInput';
import FinanceiroEdit from '../../components/Financeiro/Edit';
import { AppContext } from '../../contexts/app';
import api from '../../services/api';
import { numberToCurrency } from '../../utils/functions';
import useLancamentosAdministrativos from './useLancamentosAdministrativos';

const MESES_LABELS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const LIMIT = 10;

function LancamentosAdministrativos() {
  const navigate = useNavigate();
  const alertRef = useRef();
  const modalFinanceiroEdit = useRef();
  const { loading, notificationRef } = useContext(AppContext);

  const [page, setPage] = useState(1);
  const [dataInicial, setDataInicial] = useState(() => startOfDay(startOfMonth(new Date())));
  const [dataFinal, setDataFinal] = useState(() => endOfDay(endOfMonth(new Date())));
  const [incluirAutomaticos, setIncluirAutomaticos] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [tableFilters, setTableFilters] = useState([]);

  const formattedDataInicial = useMemo(
    () => (dataInicial ? format(dataInicial, 'yyyy-MM-dd') : ''),
    [dataInicial],
  );

  const formattedDataFinal = useMemo(
    () => (dataFinal ? format(dataFinal, 'yyyy-MM-dd') : ''),
    [dataFinal],
  );

  const filtrosQuery = useMemo(() => {
    const profissionalNome = tableFilters.find((s) => s.field === 'profissionalNome')?.value || '';
    const clienteNome = tableFilters.find((s) => s.field === 'clienteNome')?.value || '';
    const pacoteSearch = tableFilters.find((s) => s.field === 'pacoteSearch')?.value || '';
    const startValorAReceber = tableFilters.find((s) => s.field === 'startvalorAReceber')?.startRange;
    const endValorAReceber = tableFilters.find((s) => s.field === 'endvalorAReceber')?.endRange;
    const startValorAPagar = tableFilters.find((s) => s.field === 'startvalorAPagar')?.startRange;
    const endValorAPagar = tableFilters.find((s) => s.field === 'endvalorAPagar')?.endRange;

    return {
      profissionalNome: profissionalNome.trim(),
      clienteNome: clienteNome.trim(),
      pacoteSearch: pacoteSearch.trim(),
      startValorAReceber,
      endValorAReceber,
      startValorAPagar,
      endValorAPagar,
    };
  }, [tableFilters]);

  const lancamentos = useLancamentosAdministrativos({
    dataInicial: formattedDataInicial,
    dataFinal: formattedDataFinal,
    incluirAutomaticos,
    ...filtrosQuery,
  });

  useEffect(() => {
    setListLoading(true);
    Promise.resolve(lancamentos.loadPage(1, LIMIT))
      .finally(() => {
        setPage(1);
        setListLoading(false);
      });
  }, [incluirAutomaticos, lancamentos.loadPage]);

  const handleFiltrar = useCallback(() => {
    setListLoading(true);
    Promise.resolve(lancamentos.loadPage(1, LIMIT))
      .finally(() => {
        setPage(1);
        setListLoading(false);
      });
  }, [lancamentos.loadPage]);

  const handleChangePage = useCallback((newPage) => {
    setListLoading(true);
    Promise.resolve(lancamentos.loadPage(newPage, LIMIT))
      .finally(() => {
        setPage(newPage);
        setListLoading(false);
      });
  }, [lancamentos.loadPage]);

  const tipoContaFormatter = useCallback((row) => {
    const tipo = (row?.tipoConta || '').toLowerCase();
    if (tipo.includes('receber')) {
      return (
        <Badge color="success" className="w-100" tag="div">
          A Receber
        </Badge>
      );
    }
    if (tipo.includes('pagar')) {
      return (
        <Badge color="danger" className="w-100" tag="div">
          A Pagar
        </Badge>
      );
    }
    return row?.tipoConta || '-';
  }, []);

  const mesFormatter = useCallback((row) => {
    if (row?.mes && row?.ano) {
      const label = MESES_LABELS[Number(row.mes) - 1] || row.mes;
      return `${label} ${row.ano}`;
    }
    return '-';
  }, []);

  const dataFormatter = useCallback((row) => {
    if (!row?.data) return '-';
    try {
      const d = typeof row.data === 'string' ? parseISO(row.data) : new Date(row.data);
      return format(d, 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  }, []);

  const handleEdit = useCallback((row, e) => {
    if (e) e.stopPropagation();
    setListLoading(true);
    modalFinanceiroEdit.current.show(
      { _id: row._id },
      {},
      () => {
        Promise.resolve(lancamentos.loadPage(page, LIMIT))
          .finally(() => setListLoading(false));
      },
    );
  }, [page, lancamentos.loadPage]);

  const handleDelete = useCallback((row, e) => {
    if (e) e.stopPropagation();

    alertRef.current.show({
      title: 'Excluir lançamento',
      message: 'Tem certeza que deseja excluir este lançamento?',
      confirmColor: 'danger',
      confirmText: 'Excluir',
      onConfirm: async () => {
        alertRef.current.close();

        // otimista: remove da lista atual imediatamente
        const previousDatas = lancamentos.datas || [];
        lancamentos.setDatas(
          previousDatas.filter((item) => (item._id || item.id) !== (row._id || row.id)),
        );

        try {
          await api.delete(`/financeiros/${row._id}`);
        } catch (error) {
          // rollback: restaura lista anterior em caso de erro
          // eslint-disable-next-line no-console
          notificationRef.current.notify({
            message: error.response.data.message || 'Falha ao excluir lançamento',
            color: 'danger',
          });
          console.error(error);
          lancamentos.setDatas(previousDatas);
        }
      },
    });
  }, [alertRef, lancamentos.datas, lancamentos.setDatas, page]);

  const actionsFormatter = useCallback((row) => (
    <ButtonGroup size="sm">
      <Button
        color="warning"
        className="p-1"
        onClick={(e) => handleEdit(row, e)}
        title="Editar"
      >
        <i className="fa-solid fa-pen" />
      </Button>
      <Button
        color="danger"
        className="p-1"
        onClick={(e) => handleDelete(row, e)}
        title="Excluir"
      >
        <i className="fa-solid fa-trash" />
      </Button>
    </ButtonGroup>
  ), [handleEdit, handleDelete]);

  const pacoteFormatter = useCallback((row) => {
    if (!row?.pacote) return '-';
    const profissionalNome = row?.pacote?.profissional?.name;
    const clienteNome = row?.pacote?.cliente?.nome;
    const valor = numberToCurrency(row?.pacote?.valor || 0);
    const valorCliente = numberToCurrency(row?.pacote?.valorCliente || 0);

    if (!profissionalNome && !clienteNome) return '-';

    return `${profissionalNome || ''} - ${clienteNome || ''} - ${valor} / ${valorCliente}`;
  }, []);

  const columns = useMemo(
    () => [
      {
        dataField: 'lancadoPorName',
        text: 'LANÇADO POR',
        formatter: (row) => row?.lancadoPor?.name || '-',
      },
      {
        dataField: 'data',
        text: 'DATA',
        formatter: dataFormatter,
        width: '110px',
      },
      {
        dataField: 'profissional',
        text: 'PROFISSIONAL',
        textFilter: true,
        filterField: 'profissionalNome',
        formatter: (row) => row?.profissional || '-',
      },
      {
        dataField: 'paciente',
        text: 'PACIENTE',
        textFilter: true,
        filterField: 'clienteNome',
        formatter: (row) => row?.paciente || '-',
      },
      {
        dataField: 'empresa',
        text: 'EMPRESA',
        formatter: (row) => row?.empresa || '-',
      },
      {
        dataField: 'tipoLancamento',
        text: 'TIPO DE LANÇAMENTO',
        formatter: (row) => row?.tipoLancamento || '-',
      },
      {
        dataField: 'horas',
        text: 'HORAS',
        formatter: (row) => row?.horas || '-',
      },
      {
        dataField: 'tipoConta',
        text: 'TIPO DE CONTA',
        formatter: tipoContaFormatter,
        className: 'text-center',
      },
      {
        dataField: 'valorAReceber',
        text: 'VALOR A RECEBER',
        moneyRangeFilter: true,
        formatter: (row) => numberToCurrency(row?.valorAReceber),
      },
      {
        dataField: 'valorAPagar',
        text: 'VALOR A PAGAR',
        moneyRangeFilter: true,
        formatter: (row) => numberToCurrency(row?.valorAPagar),
      },
      {
        dataField: 'mes',
        text: 'MÊS',
        formatter: mesFormatter,
      },
      {
        dataField: 'tipoAtendimento',
        text: 'TIPO DE ATENDIMENTO',
        formatter: (row) => row?.tipoAtendimento || '-',
      },
      {
        dataField: 'pacote',
        text: 'PACOTE',
        textFilter: true,
        filterField: 'pacoteSearch',
        formatter: pacoteFormatter,
      },
      {
        dataField: 'obs',
        text: 'OBS',
        formatter: (row) => row?.obs || '-',
      },
      {
        dataField: 'acoes',
        text: 'AÇÕES',
        formatter: actionsFormatter,
        width: '100px',
      },
    ],
    [tipoContaFormatter, mesFormatter, dataFormatter, pacoteFormatter, actionsFormatter],
  );

  return (
    <Container fluid>
      <AlertModal ref={alertRef} />
      <FinanceiroEdit ref={modalFinanceiroEdit} />
      <Row className="mt-3">
        <Col>
          <Card>
            <CardHeader>
              <h3>Lançamentos Administrativos</h3>
            </CardHeader>
            <CardBody>
              <Row>
                <Col xs={12} md={3}>
                  <DatePickerMaskedInput
                    label="Data inicial"
                    placeholderText="Data inicial"
                    selected={dataInicial}
                    onChange={(date) => setDataInicial(date)}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <DatePickerMaskedInput
                    label="Data final"
                    placeholderText="Data final"
                    selected={dataFinal}
                    onChange={(date) => setDataFinal(date)}
                  />
                </Col>
                <Col xs={12} md={2} className="d-flex align-items-end mb-2 mb-md-0">
                  <Button color="primary mb-3" onClick={handleFiltrar} disabled={loading || listLoading}>
                    Filtrar
                  </Button>
                </Col>
                <Col xs={12} md={4} className="d-flex align-items-center">
                  <Label check className="mb-0 mr-2">
                    <Input
                      type="switch"
                      checked={incluirAutomaticos}
                      onChange={(e) => setIncluirAutomaticos(e.target.checked)}
                      disabled={loading || listLoading}
                    />
                    {' '}
                    APARECER LANÇAMENTOS AUTOMÁTICOS DO SISTEMA?
                  </Label>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Row>
                <Col xs={12} md={6} lg={3} className="mb-2">
                  <Button color="danger" block onClick={() => navigate('/conta-pagar')}>
                    Criar conta a pagar
                  </Button>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Button color="success" block onClick={() => navigate('/conta-receber')}>
                    Criar conta a receber
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <CardBody>
              <CustomTable
                columns={columns}
                onFiltersChange={setTableFilters}
                data={lancamentos.datas}
                loading={loading || listLoading}
                page={page}
                limit={LIMIT}
                totalDocs={lancamentos.totalDocs}
                totalPages={lancamentos.totalPages}
                hasPrevPage={lancamentos.hasPrevPage}
                hasNextPage={lancamentos.hasNextPage}
                paging
                onChangePage={handleChangePage}
                onRowClick={() => null}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default LancamentosAdministrativos;
